import { DataItem } from 'scrivito'

import {
  TimeRecordClient,
  TimeRecordOrder,
  TimeRecordPosition,
  CustomerWithOrders,
} from '../types'
import { TimerecordClient } from '@/Data/TimerecordClient/TimerecordClientDataClass'
import { TimerecordOrder } from '@/Data/TimerecordOrder/TimerecordOrderDataClass'
import { TimerecordPosition } from '@/Data/TimerecordPosition/TimerecordPositionDataClass'
import { TimerecordUser } from '@/Data/TimerecordUser/TimerecordUserDataClass'
import { TimerecordUtilization } from '@/Data/TimerecordUtilization/TimerecordUtilizationDataClass'
import { NO_LIMIT } from '@/Widgets/TimeTrackingOverviewWidget/const'

export class TimeRecordDataService {
  static getClientsForUser(userId: string): TimeRecordClient[] {
    const scope = TimerecordClient.all().transform({
      filters: { user: userId },
      ...NO_LIMIT,
    })
    return scope.take().map((client) => ({
      id: client.id(),
      name: client.get('name') as string,
    }))
  }

  static getClients(): TimeRecordClient[] {
    return TimerecordClient.all()
      .take()
      .map((client) => ({
        id: client.id(),
        name: client.get('name') as string,
      }))
  }

  static getAllClients(): TimeRecordClient[] {
    return TimerecordClient.all()
      .transform({
        filters: { user: '*' },
      })
      .take()
      .map((client) => ({
        id: client.id(),
        name: client.get('name') as string,
      }))
  }

  static getOrders(clientId: string): TimeRecordOrder[] {
    return TimerecordOrder.all()
      .transform({
        filters: { client: { operator: 'equals', value: clientId } },
        ...NO_LIMIT,
      })
      .take()
      .map((order) => ({
        id: order.id(),
        name: order.get('name') as string,
        client: order.get('client') as string,
        packages: [],
      }))
  }

  static getPositions(orderId: string): TimeRecordPosition[] {
    return TimerecordPosition.all()
      .transform({
        filters: { order: { operator: 'equals', value: orderId } },
        ...NO_LIMIT,
      })
      .take()
      .map((position: DataItem) => ({
        id: position.id(),
        name: position.get('name') as string,
        order: position.get('order') as string,
        position: position.get('position') as string,
        budget: position.get('budget') as string,
        open: position.get('open') as string,
      }))
  }

  static getClientsByIds(ids: string[]): DataItem[] {
    if (!ids.length) return []
    return TimerecordClient.all()
      .transform({ filters: { ids: ids.join(',') }, ...NO_LIMIT })
      .take()
  }

  static getOrdersByIds(ids: string[]): DataItem[] {
    if (!ids.length) return []
    return TimerecordOrder.all()
      .transform({ filters: { ids: ids.join(',') }, ...NO_LIMIT })
      .take()
  }

  static getPositionsByIds(ids: string[]): DataItem[] {
    return ids
      .map((id) => {
        try {
          return TimerecordPosition.get(id)
        } catch {
          return null
        }
      })
      .filter((item): item is DataItem => item !== null)
  }

  static getUtilization(
    userId: string,
    dateFilters: Record<string, string>,
  ): DataItem[] {
    return TimerecordUtilization.all()
      .transform({
        filters: { user: userId, ...dateFilters },
        ...NO_LIMIT,
      })
      .take()
  }

  static getCustomersWithOrders(
    accountDirectorId: string,
  ): CustomerWithOrders[] {
    const clients = this.getClientsForUser(accountDirectorId)

    return clients.map((client) => ({
      label: client.name,
      id: client.id,
      value: client.id,
      orders: this.getOrders(client.id),
    }))
  }

  static getOrdersForAccountDirectors(): DataItem[] {
    const userIds = TimerecordUser.all()
      .transform({ filters: { accountDirector: true } })
      .take()
      .map((user) => user.id())

    const ordersById = new Map<string, DataItem>()
    userIds.forEach((userId) => {
      TimerecordOrder.all()
        .transform({ filters: { accountDirector: userId } })
        .take()
        .forEach((order) => ordersById.set(order.id(), order))
    })

    return Array.from(ordersById.values())
  }
}
